'use client';

import { useEffect, useRef } from 'react';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { toast } from '@ps/ui/atoms/sonner';

import { useBioUserAccess } from '../../../bio/contexts/user-access/useBioUserAccess';
import { useOnCheckoutSuccess } from '../../../checkout/notification/useOnCheckoutSuccess';
import type { PostViewModel } from '../types';
import {
  type PostUnlockActions,
  usePostUnlockActions,
} from './usePostUnlockActions';
import { useUnredactedPost } from './useUnredactedPost';

export interface PostUnlock extends PostUnlockActions {
  /** The (possibly re-fetched, un-redacted) post the gate should render. */
  post: PostViewModel;
}

/**
 * Drives post unlock (subscribe / pay / tip) for a gated post — the Next mirror
 * of PopshopRN's `postUnlocked$` continuation (`flows/bio/wall/components/post`).
 *
 * The CTAs ({@link usePostUnlockActions}) never mutate the post directly: each
 * launches a checkout as a SOFT nav (the post stays mounted underneath), with
 * `redirect_to` defaulting to the post's own url. On success the checkout engine
 * notifies through {@link useOnCheckoutSuccess}; this hook re-resolves:
 *   - **subscribe** → `useBioUserAccess().refresh()` raises the viewer's tier,
 *     which flips `useUnredactedPost`'s reactive re-fetch (level-gated);
 *   - **pay / tip** → order-backed entitlement the tier can't express, so force
 *     a re-fetch via `reload()` (which polls past the search-index lag).
 * When the re-fetched post comes back un-restricted, the paywall lifts and a
 * "Post unlocked!" toast fires on the restricted→unlocked transition.
 */
export function usePostUnlock(
  initial: PostViewModel,
  sellerId: string,
  bioContext: BioContext | null,
): PostUnlock {
  const { refresh } = useBioUserAccess();
  const { post, reload } = useUnredactedPost(initial, sellerId, bioContext);

  // Armed ONLY by a checkout-success unlock for this post — gates the toast so it
  // never fires on the passive redacted→unredacted resolution that happens on
  // every visit to an already-unlocked post (owner / already-paid / entitled).
  const unlockToastPending = useRef(false);

  const urlCode = initial.author.handle;
  const actions = usePostUnlockActions({
    postId: initial.id,
    urlCode,
    sellerId,
    bioContext,
    // "Next url defaults to the post url" — return to the post after checkout.
    redirectTo: initial.permalinkHref,
  });

  // Success continuation — the checkout is a soft nav, so the post stays mounted
  // and this notification is what lifts the gate on return.
  useOnCheckoutSuccess(event => {
    if (
      (event.type === 'post' && event.params.post_id === initial.id) ||
      (event.type === 'tip' && event.params.tip_ref === initial.id)
    ) {
      unlockToastPending.current = true;
      reload();
    } else if (event.type === 'community_membership_subscription') {
      // A tier grant — re-resolve the viewer's standing (raises the level, which
      // the reactive re-fetch keys on) and force a poll for un-restriction.
      unlockToastPending.current = true;
      refresh();
      reload();
    }
  });

  // Unlock toast on the restricted → unlocked transition, but ONLY when a
  // checkout success armed it — never on the passive redacted→unredacted
  // resolution that fires on every visit to an already-unlocked post.
  const wasRestricted = useRef(initial.restricted);
  useEffect(() => {
    if (
      unlockToastPending.current &&
      wasRestricted.current &&
      !post.restricted
    ) {
      toast.success('Post unlocked! Enjoy your exclusive access!');
      unlockToastPending.current = false;
    }
    wasRestricted.current = post.restricted;
  }, [post.restricted]);

  return { post, ...actions };
}
